import { memo } from 'react';
import useStoreAndLanguageParams from '../../core/hooks/useStoreAndLanguageParams';
import useCurrentStockOnlyFlag from '../hooks/useCurrentStockOnlyFlag';
import useStockMessage from '../hooks/useStockMessage';
import type {
    FutureStockQuantities,
    ProductOrderRestriction,
    StockQuantity,
} from '../types';
import { StockStatuses } from '../types';
import getFutureStocksAfterQuantityReductions from '../util/getFutureStocksAfterQuantityReductions';
import getStockAfterQuantityReductions from '../util/getStockAfterQuantityReductions';
import getStockStatus from '../util/getStockStatus';
import ProductStockIndicatorContent from './ProductStockIndicatorContent';

const StockIndicator = ({
    desiredQuantity = 1,
    branchName,
    location,
    messageStyle,
    lineIndicator = false,
    hideBranchesWithoutAvailability = false,
    hideBranchCode = false,
    isSecondaryBranch = false,
    stock: originalStock,
    futureStock: originalFutureStock,
    restriction,
    loading = false,
    quantityToIgnore = 0,
}: {
    desiredQuantity?: number;
    branchName: string;
    location: string;
    messageStyle: 'left' | 'right' | 'tooltip' | 'none';
    lineIndicator?: boolean;
    hideBranchesWithoutAvailability?: boolean;
    hideBranchCode?: boolean;
    isSecondaryBranch?: boolean;
    stock: StockQuantity;
    futureStock?: FutureStockQuantities;
    restriction?: ProductOrderRestriction;
    loading?: boolean;
    quantityToIgnore?: number;
}) => {
    const { store } = useStoreAndLanguageParams();
    const currentStockOnly = useCurrentStockOnlyFlag();

    const { stock, quantityToIgnore: quantityLeftToIgnore } =
        getStockAfterQuantityReductions(originalStock, quantityToIgnore);

    const { futureStock } = getFutureStocksAfterQuantityReductions(
        originalFutureStock,
        quantityLeftToIgnore
    );

    const stockMessage = useStockMessage({
        stock,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        futureStock: futureStock ? [futureStock] : [],
        restriction,
        desiredQuantity,
    });

    const statuses = getStockStatus({
        store,
        stock,
        futureStock,
        restriction,
        desiredQuantity,
        currentStockOnly,
    });

    const restrictionId = statuses.find(
        item => item.status === StockStatuses.RESTRICTED_FOR_ORDER
    )?.restrictionId;

    const plainStatuses = statuses.slice(0, 2).map(({ status }) => status);

    return (
        <ProductStockIndicatorContent
            loading={loading}
            restrictionId={restrictionId}
            branchName={branchName}
            lineIndicator={lineIndicator}
            statuses={plainStatuses}
            stockMessage={stockMessage}
            messageStyle={messageStyle}
            location={location}
            hideBranchesWithoutAvailability={hideBranchesWithoutAvailability}
            hideBranchCode={hideBranchCode}
            isSecondaryBranch={isSecondaryBranch}
        />
    );
};

export default memo(StockIndicator);
